import { useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../components/PageTitle";
import PositiveButton from "../../components/PositiveButton";
import AddIcon from "@mui/icons-material/Add";
import { Campaign } from "../../types/Campaigns";
import { PagedResults } from "../../types/PagedResults";
import { useEffect, useState } from "react";
import useCampaignsService from "../../services/CampaignsService";
import Ghost from "../../components/Ghost";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";

export default function AdminCampaignsListPage() {
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<PagedResults<Campaign> | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    searchValue: searchParams.get("searchValue") || "",
    page: parseInt(searchParams.get("page") || "1")
  });

  const { searchCampaigns } = useCampaignsService();
  const debounceDelay = 500;

  const search = () => {
    setIsSearching(true);

    setSearchParams({
      searchValue: formData.searchValue,
      page: formData.page.toString(),
    });

    searchCampaigns(formData.searchValue, formData.page, 10)
      .then((searchResults) => {
        setResults(searchResults);
      })
      .catch((error) => {
        console.error("Error searching campaigns:", error);
      })
      .finally(() => {
        setIsSearching(false);
      });
    };

    const handleInputChange = (e: any) => {
      const { name, value } = e.target;

      setFormData({
        ...formData,
        [name]: value,
        page: 1, // Reset to the first page when searching
      });
    };

    const handlePageChange = (newPage: number) => {
      setFormData((prev) => ({
        ...prev,
        page: newPage,
      }));
    };

    useEffect(() => {
      const handler = setTimeout(() => {
        search();
      }, debounceDelay);

      return () => {
        clearTimeout(handler);
      };
    }, [formData.searchValue, formData.page]);

    useEffect(() => {
      search();
    }, []);

  return (
    <div className="flex flex-col gap-4">
      <section role="heading" className="flex flex-row">
        <PageTitle className="mr-4">Campaigns</PageTitle>
        <PositiveButton onClick={() => navigate("/admin/campaigns/new")}>
          <AddIcon /> Add new campaign
        </PositiveButton>
        <div
          role="searchbox"
          className="flex flex-row gap-2  px-4 items-center xl:col-span-2 flex-grow"
        >
          <label
            htmlFor="search"
            className="hidden xl:w-auto xl:block leading-6"
          >
            Filter:
          </label>
          <div className="mt-2 w-full">
            <input
              type="text"
              name="searchValue"
              placeholder="Search term"
              value={formData.searchValue}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded text-black"
            />
          </div>
        </div>
      </section>
      <section role="content">
        <table className="table-auto w-full card border-spacing-2">
          <thead>
            <tr className="">
              <th className="font-normal text-left p-4">Image</th>
              <th className="font-normal text-left p-4">Title</th>
              <th className="font-normal text-left p-4">Start time</th>
              <th className="font-normal text-left p-4">Approved?</th>
              <th className="font-normal text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {isSearching &&
              Array.from({ length: 3 }, (_, i) => (
                <tr className="border-t border-cyan-900" key={i}>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                </tr>
              ))}

            {!isSearching && results && (
              <>
                {!results.items || results.items.length === 0 ? (
                  <tr className="border-t border-cyan-900">
                    <td className="text-left p-4" colSpan={5}>
                      No results found.
                    </td>
                  </tr>
                ) : (
                  results.items.map((campaign) => (
                    <tr
                      className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer"
                      key={campaign.id}
                      onClick={() => navigate(`/admin/campaigns/${campaign.id}/`)}
                      title={`View campaign ${campaign.title}`}
                    >
                      <td className="text-left p-4"><img src={campaign.campaignImageUrl} alt={campaign.title} className="w-32 h-18 object-cover rounded" /></td>
                      <td className="text-left p-4">{campaign.title}</td>
                      <td className="text-left p-4">{campaign.startTime.toString()}</td>
                      <td className="text-left p-4">{campaign.isApproved}</td>
                      <td className="p-4 w-auto text-right">
                        <ArrowForwardTwoToneIcon />
                      </td>
                    </tr>
                  ))
                )}
              </>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
